import _ from 'lodash'
import client, {fetch, post} from '@/base-http'
import {MediaObject, MediaDeletionResult, MediaUpload, UploadConstrains} from '@/modules/media/types'
import KeyValuePair from '@/modules/core/support/KeyValuePair'
import Axios from 'axios'

export class MediaListFilters {
    public type = ''
    public tags: string[] = []
    public name = ''
    public creatorName = ''
    public includeDeleted = false
}

/**
 * Obtiene una lista de objetos multimedia
 */
export const listMedia = async (filters = new MediaListFilters()): Promise<MediaObject[]> => {
    const response = await client.get('media/objects', {
        params: {
            type: filters.type || undefined,
            tags: (filters.tags && filters.tags.length) ? filters.tags.join(',') : undefined,
            name: filters.name || undefined,
            creatorName: filters.creatorName || undefined,
            includeDeleted: filters.includeDeleted ? 1 : undefined,
        },
    })

    return response.data.map((mo: KeyValuePair) => MediaObject.convert(mo))
}

/**
 * Permite obtener un objeto multimedia por identificador
 *
 * @param id
 */
export async function getMedia (id: number) {
    const data = await fetch(`media/objects/${id}`)

    return MediaObject.convert(data)
}

/**
 * Actualiza un objeto multimedia
 *
 * @param media
 */
export const updateMediaObject = async (media: MediaObject) => {
    const response = await client.put(`media/objects/${media.id}`, media.serialize())

    return MediaObject.convert(response.data)
}

/**
 * Elimina un objeto multimedia
 *
 * @param media
 */
export const deleteMediaObject = async (media: MediaObject) => {
    try {
        const response = await client.delete(`media/objects/${media.id}`)

        return response.data.success || false
    } catch (e) {
        throw new Error(_.get(e, 'response.data.message', e.message))
    }
}

/**
 * Elimina una lista de objetos multimedia
 *
 * @param  {number[]} list
 * @returns Promise
 */
export const deleteMediaList = async (list: number[]): Promise<MediaDeletionResult> => {
    const response = await client.delete('media/objects', {
        params: {
            list: JSON.stringify(list) || '[]',
        },
    })

    return response.data
}

/**
 * Obtiene la lista de etiquetas multimedia
 *
 * @param criteria
 */
export async function getTagList (criteria = {similar: ''}): Promise<string[]> {
    const data = await fetch('media/tags', {
        params: criteria,
    })

    if (!Array.isArray(data)) {
        throw new Error('Se esperaba un array con la lista de etiquetas')
    }

    return data
}


export interface UploadOptions {
    upload: MediaUpload
    onProgress?: () => void
    customThumbnails?: boolean
}

/**
 * Subir archivo al servidor
 *
 * @param uploads
 */
export function upload (uploads: UploadOptions[]) {
    // Construye una petición Http por cada archivo que será enviado al servidor
    const buildRequest = (options: UploadOptions) => {
        const upload = options.upload
        const data = new FormData()

        const file = upload.optimizedFile || upload.file
        if (!file) {
            throw new Error('Las opciones no incluyen el archivo que será subido al servidor')
        }

        data.set('file', file)
        data.set('media_object', JSON.stringify(upload.media.serialize()))
        const params: KeyValuePair = {}
        // Si la opción "customThumbnails" se activa el cliente enviará las miniaturas
        // personalizadas
        if (options.customThumbnails) {
            params['custom_thumbnails'] = 'true'
            upload.thumbnails.forEach((file, size) => {
                data.set(`thumbnail_${size}`, file)
            })
        }

        return client
            .post('/media/file', data, { params })
            .then((response => MediaObject.convert(response.data)))
    }

    const promiseMap = new Map<string, Promise<MediaObject>>()
    const promises: Array<Promise<MediaObject>> = []
    uploads.forEach(upload => {
        const promise = buildRequest(upload)
        promiseMap.set(upload.upload.id, promise)
        promises.push(promise)
    })

    return {
        requests: promiseMap, master: Axios.all(promises),
    }
}

/**
 * Solicita al servidor los límites para la subidas de archivos
 */
export async function getUploadConstrains () {
    const data = await fetch('/media/upload/constrains')

    return UploadConstrains.deserialize(data)
}

/**
 * Actualiza el archivo asociado a un objeto multimedia
 */
export async function updateMediaObjectFile (id: number, file: Blob) {
    const data = new FormData()
    data.append('file', file)
    const media = await post(`/media/objects/${id}/file`, data)

    return MediaObject.convert(media)
}
